import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { DateTime } from 'luxon';
import { toast } from 'react-toastify';
import Icon from '@mdi/react';
import { mdiAirplaneTakeoff } from '@mdi/js';
import LocationPickerModal from './LocationPickerModal';
import DateInputWithPicker from './DateInputWithPicker';
import InfoHelp from './InfoHelp';
import { availabilityApi } from '../api/availability';
import { currencyOptions } from '../config/currencyOptions';
import { formatCurrency } from '../utils/currency';
import { estimateConversion } from '../utils/exchangeEstimate';
import './TravelPeriodModal.css';


// How many blocking bookings to list under the locked currency picker.
const LOCKED_LIST_LIMIT = 5;

const TravelPeriodModal = ({ isOpen, onClose, onSave, initialData = null, defaultCurrency = 'AUD', defaultRate = '', teachingLocations = [], onRequestUnlockCancel, lockRefreshKey = 0 }) => {
    const navigate = useNavigate();
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [showLocationPicker, setShowLocationPicker] = useState(false);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [privatePrice, setPrivatePrice] = useState('');
    const [selectedCurrency, setSelectedCurrency] = useState(defaultCurrency);
    const [defaultLocationId, setDefaultLocationId] = useState('');
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    // Inline confirm-mode body for the currency confirm step (AC9). Toggles the modal
    // body + footer instead of nesting a second modal (this is a legacy custom modal,
    // not a BaseModal).
    const [confirmMode, setConfirmMode] = useState(false);
    // Edit mode: the period's active bookings (same "active" definition as the backend
    // currency lock). null = not loaded yet. Any booking locks the currency.
    const [activeBookings, setActiveBookings] = useState(null);
    const [localRefreshKey, setLocalRefreshKey] = useState(0);

    const isEditMode = initialData !== null;

    // Travel-currency intent flags (drive the mandate + heads-up gates below).
    // A "foreign" travel currency is anything other than the org base currency.
    const isForeignCurrency = selectedCurrency !== defaultCurrency;
    // On load an unpriced period sets selectedCurrency to defaultCurrency, so compare
    // against that same baseline to detect a real currency change while editing.
    const initialCurrency = isEditMode ? (initialData.currency || defaultCurrency) : null;
    const currencyChangedOnEdit = isEditMode && initialCurrency !== selectedCurrency;
    // Currency lock (AC9): a period with an active booking can't change currency.
    const isCurrencyLocked = isEditMode && (activeBookings?.length || 0) > 0;
    // Saving a currency other than the Base Currency, newly chosen (create, or changed on
    // edit), asks the instructor to confirm first: it locks once anyone books.
    const needsCurrencyConfirm = isForeignCurrency && (!isEditMode || currencyChangedOnEdit);
    // Rough "what you'd receive" for the confirm screen (estimate only; real conversion is
    // at Stripe's rate on the day each student pays).
    const ratePerHour = parseFloat(privatePrice);
    const estimatedPayout = isForeignCurrency ? estimateConversion(ratePerHour, selectedCurrency, defaultCurrency) : null;
    // True only when the instructor has no default rate to pre-fill — the one genuinely
    // "unset" case, surfaced as a non-blocking inline link (not a warning).
    const hasNoDefaultRate = !isEditMode && !defaultRate;

    // Populate form when editing
    useEffect(() => {
        if (isOpen && initialData) {
            setSelectedLocation(initialData.location || null);
            setStartDate(initialData.startDate || '');
            setEndDate(initialData.endDate || '');
            setPrivatePrice(initialData.privatePrice ? String(initialData.privatePrice) : '');
            setSelectedCurrency(initialData.currency || defaultCurrency);
            setDefaultLocationId(initialData.defaultLocationId || '');
        } else if (isOpen && !initialData) {
            // New travel period — pre-fill the instructor's default rate + home currency
            // as the editable override. Leaving them as-is accepts the default.
            setSelectedCurrency(defaultCurrency);
            setPrivatePrice(defaultRate ? String(defaultRate) : '');
        }
    }, [isOpen, initialData, defaultCurrency, defaultRate]);

    // Edit mode: load the period's active bookings to decide the currency lock. Re-runs
    // after the bookings are cancelled from the unlock modal (lockRefreshKey) or after a
    // save was refused because a booking landed meanwhile (localRefreshKey).
    useEffect(() => {
        if (!isOpen || !initialData?.id) return;
        let cancelled = false;
        availabilityApi.getTravelPeriodBookings(initialData.id)
            .then(bookings => { if (!cancelled) setActiveBookings(bookings || []); })
            .catch(err => {
                console.error('Failed to load travel period bookings:', err);
                // Unknown: leave the picker unlocked; the backend lock still refuses a
                // real change and the 409 path below switches the picker to locked.
                if (!cancelled) setActiveBookings([]);
            });
        return () => { cancelled = true; };
    }, [isOpen, initialData?.id, lockRefreshKey, localRefreshKey]);

    // Reset form when modal closes
    useEffect(() => {
        if (!isOpen) {
            setSelectedLocation(null);
            setStartDate('');
            setEndDate('');
            setPrivatePrice('');
            setSelectedCurrency(defaultCurrency);
            setDefaultLocationId('');
            setErrors({});
            setConfirmMode(false);
            setActiveBookings(null);
        }
    }, [isOpen, defaultCurrency]);

    const validateForm = () => {
        const newErrors = {};

        if (!selectedLocation) {
            newErrors.location = 'Location is required';
        }

        if (!startDate) {
            newErrors.startDate = 'Start date is required';
        }

        if (!endDate) {
            newErrors.endDate = 'End date is required';
        }

        if (startDate && endDate) {
            const start = DateTime.fromISO(startDate);
            const end = DateTime.fromISO(endDate);

            if (end < start) {
                newErrors.endDate = 'End date must be after start date';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleLocationSave = (locationData) => {
        setSelectedLocation(locationData);
        setShowLocationPicker(false);
        setErrors(prev => ({ ...prev, location: null }));
    };

    const handleSave = async (e) => {
        // Prevent default form submission and multiple clicks
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }

        if (isLoading) return; // Prevent multiple submissions

        // W1 — rate-with-currency mandate (mirrors backend B2): a foreign travel
        // currency can only be charged if a positive rate is entered. This is also what
        // stops a LOCKED foreign-currency period from clearing its rate (the picker is
        // disabled on the stored currency, so clearing always lands here). Block with an
        // inline field error (above the input) + a toast. Return before validateForm
        // so the field error isn't wiped by its setErrors reset.
        const hasPositiveRate = !!privatePrice && parseFloat(privatePrice) > 0;
        if (isForeignCurrency && !hasPositiveRate) {
            const msg = `Set a private rate to charge in ${selectedCurrency}.`;
            setErrors(prev => ({ ...prev, privatePrice: msg }));
            toast.error(msg);
            return;
        }

        if (!validateForm()) return;

        // Currency confirm step (AC9) — inline confirm-mode body; "Confirm currency" calls
        // doSave directly. The Base Currency (the normal case) skips it.
        if (!confirmMode && needsCurrencyConfirm) {
            setConfirmMode(true);
            return;
        }

        await doSave();
    };

    const doSave = async () => {
        if (isLoading) return;
        setIsLoading(true);

        try {
            // On CREATE the rate + currency are pre-filled with the instructor's default
            // as an editable OVERRIDE. If the user leaves BOTH untouched (rate === default,
            // currency === home), there is no override — persist null so the trip keeps
            // INHERITING the default rate. Storing the pre-filled value instead would pin
            // the trip to today's rate, so a later default-rate change wouldn't apply to
            // new bookings in this window. On EDIT we never collapse: the field holds the
            // stored value or a deliberate edit, so persist exactly what's shown (a
            // genuine override that coincidentally equals the default stays an override).
            const acceptedDefaultOnCreate =
                !isEditMode && !!defaultRate && privatePrice === String(defaultRate) && !isForeignCurrency;
            const isOverride = !!privatePrice && !acceptedDefaultOnCreate;

            const travelPeriod = {
                destination: selectedLocation.description, // Required by backend
                location: {
                    description: selectedLocation.description,
                    address: selectedLocation.address,
                    latitude: selectedLocation.latitude,
                    longitude: selectedLocation.longitude,
                },
                startDate,
                endDate,
                // Send null (NOT undefined) when there is no override — undefined is
                // dropped by JSON serialization, so the backend would never see the
                // clear and any stored rate would persist. null reaches the backend,
                // which $unsets the rate so future bookings inherit the home rate.
                privatePrice: isOverride ? parseFloat(privatePrice) : null,
                currency: isOverride ? selectedCurrency : null, // Only send currency for a real override
                defaultLocationId: defaultLocationId || null, // Reference to teaching location, null = TBA
            };

            await onSave(travelPeriod);
            // Hook owns all modal lifecycle (both create and edit paths)
        } catch (error) {
            console.error('Error saving travel period:', error);

            // A booking landed after the modal opened: keep the modal open, show why, and
            // switch the picker to locked. Branch on the code, never the message text.
            if (error.errorCode === 'TRAVEL_CURRENCY_LOCKED') {
                toast.error(error.message);
                setConfirmMode(false);
                setSelectedCurrency(initialCurrency);
                setLocalRefreshKey(k => k + 1);
                return;
            }

            // Extract specific error message from backend
            let errorMessage = 'Failed to save travel period. Please try again.';

            if (error.response?.data?.error?.message) {
                // V2 API structured error
                errorMessage = error.response.data.error.message;
            } else if (error.response?.data?.message) {
                // Alternative error format
                errorMessage = error.response.data.message;
            } else if (error.message) {
                // Generic error
                errorMessage = error.message;
            }

            // Set error for display in modal (if modal stays open briefly)
            setErrors({ submit: errorMessage });

            // Close modal and let parent display error in header status message
            onClose();

            // Re-throw with formatted message so parent can catch and display
            throw new Error(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="travel-period-modal-overlay" onClick={onClose}>
            <div className="travel-period-modal" onClick={(e) => e.stopPropagation()}>
                <div className="travel-period-modal-header">
                    <div className="travel-period-modal-header-content">
                        <Icon path={mdiAirplaneTakeoff} size={1.2} color="var(--dl-color-purple-900)" />
                        <h2>{isEditMode ? 'Edit Travel Period' : 'Create Travel Period'}</h2>
                    </div>
                    <button type="button" className="travel-period-modal-close" onClick={onClose}>×</button>
                </div>

                <form onSubmit={handleSave}>
                    <div className="travel-period-modal-body">
                    {confirmMode ? (
                        <div className="travel-period-confirm">
                            <h3 className="travel-period-confirm-title">Charging in {selectedCurrency}</h3>
                            <ul className="travel-period-confirm-points">
                                <li>
                                    Students pay in {selectedCurrency}, which usually avoids a currency conversion fee from their bank.
                                </li>
                                <li>
                                    You're paid in {defaultCurrency}, at the exchange rate on the day each student pays
                                    {estimatedPayout ? ` (${formatCurrency(ratePerHour, selectedCurrency)} is about ${formatCurrency(Math.round(estimatedPayout), defaultCurrency)} today)` : ''}.
                                </li>
                                <li>Locked once anyone books. To change it after that, cancel those bookings first.</li>
                            </ul>
                        </div>
                    ) : (
                    <>
                    {errors.submit && (
                        <div className="error-message" style={{ marginBottom: '16px' }}>
                            {errors.submit}
                        </div>
                    )}

                    <div className="form-group">
                        <label htmlFor="location">
                            Travel Destination <span className="required">*</span>
                        </label>
                        {selectedLocation ? (
                            <div className="selected-location-display">
                                <div className="location-display-content">
                                    <div className="location-display-description">{selectedLocation.description}</div>
                                    {selectedLocation.address && (
                                        <div className="location-display-address">{selectedLocation.address}</div>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setShowLocationPicker(true)}
                                    className="btn-change-location"
                                >
                                    Change
                                </button>
                            </div>
                        ) : (
                            <button
                                type="button"
                                onClick={() => setShowLocationPicker(true)}
                                className="btn-select-location"
                            >
                                + Select Travel Destination
                            </button>
                        )}
                        {errors.location && (
                            <span className="field-error">{errors.location}</span>
                        )}
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="startDate">
                                Start Date <span className="required">*</span>
                            </label>
                            <DateInputWithPicker
                                id="startDate"
                                value={startDate}
                                onChange={(isoDate) => {
                                    setStartDate(isoDate);
                                    setErrors(prev => ({ ...prev, startDate: null }));
                                }}
                                error={!!errors.startDate}
                                required
                            />
                            {errors.startDate && (
                                <span className="field-error">{errors.startDate}</span>
                            )}
                        </div>

                        <div className="form-group">
                            <label htmlFor="endDate">
                                End Date <span className="required">*</span>
                            </label>
                            <DateInputWithPicker
                                id="endDate"
                                value={endDate}
                                onChange={(isoDate) => {
                                    setEndDate(isoDate);
                                    setErrors(prev => ({ ...prev, endDate: null }));
                                }}
                                error={!!errors.endDate}
                                required
                                // Open on the chosen start date's month when no end date
                                // is set yet — avoids defaulting to the current month.
                                fallbackMonth={startDate ? DateTime.fromISO(startDate) : null}
                            />
                            {errors.endDate && (
                                <span className="field-error">{errors.endDate}</span>
                            )}
                        </div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="defaultLocation" className="form-label">
                            Default Teaching Venue <span style={{ color: '#666', fontWeight: 'normal' }}>(Optional)</span>
                        </label>
                        <p className="form-hint">
                            Select a venue if you know where you'll be teaching. Leave blank if TBA.
                        </p>
                        <p className="form-hint travel-period-fx-hint">
                            <InfoHelp
                                label="Adding and changing venues"
                                title="Travel venues"
                                ariaLabel="About adding and changing travel venues">
                                <p>
                                    Add a new venue in your <Link to="/instructor-profile-setup/profile" target="_blank" rel="noopener noreferrer">instructor profile</Link> (opens in a new tab) under <strong>Timezones &amp; Teaching Locations</strong>.
                                </p>
                                <p>
                                    You can change this field later. The venue you choose applies to all bookings in this travel period.
                                </p>
                            </InfoHelp>
                        </p>
                        <select
                            id="defaultLocation"
                            className="form-select"
                            value={defaultLocationId}
                            onChange={(e) => setDefaultLocationId(e.target.value)}
                        >
                            <option value="">No venue selected (TBA)</option>
                            {teachingLocations
                                .filter(loc => loc.isActive && loc.isTravel === true)
                                .map(loc => (
                                    <option key={loc.id} value={loc.id}>
                                        {loc.description}
                                    </option>
                                ))}
                        </select>
                    </div>

                    <div className="form-group">
                        <label htmlFor="privatePrice" className="form-label">
                            Override Your Default Rate &amp; Currency
                        </label>
                        <p className="form-hint">
                            {hasNoDefaultRate
                                ? 'Enter a private lesson rate for this trip, or set a default rate on your instructor profile to pre-fill it.'
                                : `These are pre-filled with your default rate and your Base Currency (${defaultCurrency}). Change them only if this trip needs a different rate or currency.`}
                        </p>
                        <p className="form-hint travel-period-fx-hint">
                            <InfoHelp
                                label="How charging in another currency works"
                                title="Charging in another currency"
                                ariaLabel="How charging in another currency works">
                                <h4>If you charge in your Base Currency ({defaultCurrency})</h4>
                                <p>
                                    You receive exactly the rate you set. Students whose cards are in another currency pay in {defaultCurrency}, so their own bank converts it and usually adds a foreign transaction fee, often 2 to 3%. You don't see that fee, but they do.
                                </p>
                                <h4>If you charge in another currency</h4>
                                <p>
                                    Students see and pay in that currency, which usually avoids a conversion fee from their bank. Your payout still arrives in {defaultCurrency}: each payment is converted at the exchange rate on the day the student pays, so the amount you receive goes up or down with the exchange rate.
                                </p>
                                <p>
                                    You don't pay a conversion fee. It is covered by the service fee students pay at checkout.
                                </p>
                                <h4>Changing it later</h4>
                                <p>
                                    Once anyone books a lesson in this travel period, its currency is locked. To change it, cancel those bookings first. You can change the rate at any time; a new rate applies to new bookings only.
                                </p>
                                <h4>Your Base Currency</h4>
                                <p>
                                    Your Base Currency ({defaultCurrency}) is the currency you are paid in. You can see it in <Link to="/settings" target="_blank" rel="noopener noreferrer">Settings</Link> (opens in a new tab) under <strong>Account Details → Base Currency</strong>. It matches the country of your payout bank account.
                                </p>
                            </InfoHelp>
                        </p>
                        {isCurrencyLocked && (
                            <div className="travel-period-currency-lock">
                                <p className="form-hint">
                                    This travel period's currency is locked because it has {activeBookings.length} booking{activeBookings.length !== 1 ? 's' : ''}. To change it, cancel those bookings first. You can still change the rate; a new rate applies to new bookings only.
                                </p>
                                <ul className="travel-period-lock-list">
                                    {activeBookings.slice(0, LOCKED_LIST_LIMIT).map(b => (
                                        <li key={b.id}>
                                            {DateTime.fromISO(b.bookingDate).toFormat('dd MMM')} at {b.startTime}
                                            {(b.studentName || b.studentEmail) && <> · {b.studentName || b.studentEmail}</>}
                                        </li>
                                    ))}
                                    {activeBookings.length > LOCKED_LIST_LIMIT && (
                                        <li>and {activeBookings.length - LOCKED_LIST_LIMIT} more</li>
                                    )}
                                </ul>
                                {onRequestUnlockCancel && (
                                    <button
                                        type="button"
                                        className="btn-turquoise travel-period-unlock-btn"
                                        onClick={() => onRequestUnlockCancel(activeBookings)}>
                                        Cancel these bookings
                                    </button>
                                )}
                            </div>
                        )}
                        {hasNoDefaultRate && (
                            <p className="form-hint travel-period-set-rate-link">
                                <button
                                    type="button"
                                    className="travel-period-inline-link"
                                    onClick={() => navigate('/instructor-profile-setup/profile')}
                                >
                                    Set your default rate
                                </button>
                            </p>
                        )}
                        <div className="form-row">
                            <div className="input-with-prefix">
                                <span className="prefix">{currencyOptions.find(c => c.code === selectedCurrency)?.symbol || '$'}</span>
                                <input
                                    type="number"
                                    id="privatePrice"
                                    value={privatePrice}
                                    onChange={(e) => {
                                        setPrivatePrice(e.target.value);
                                        setErrors(prev => ({ ...prev, privatePrice: null }));
                                    }}
                                    placeholder="0.00"
                                    min="0"
                                    step="1"
                                />
                            </div>
                            <select
                                id="currency"
                                className="form-select"
                                value={selectedCurrency}
                                onChange={(e) => setSelectedCurrency(e.target.value)}
                                disabled={isCurrencyLocked}
                            >
                                {currencyOptions.map(currency => (
                                    <option key={currency.code} value={currency.code}>
                                        {currency.symbol} {currency.code} - {currency.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        {errors.privatePrice && (
                            <span className="field-error">{errors.privatePrice}</span>
                        )}
                    </div>
                    </>
                    )}
                    </div>

                    <div className="travel-period-modal-footer">
                        {confirmMode ? (
                            <>
                                <button
                                    type="button"
                                    className="btn-cancel"
                                    onClick={() => setConfirmMode(false)}
                                    disabled={isLoading}
                                >
                                    Back
                                </button>
                                <button
                                    type="button"
                                    className="btn-primary"
                                    onClick={() => doSave()}
                                    disabled={isLoading}
                                >
                                    {isLoading ? 'Saving...' : 'Confirm currency'}
                                </button>
                            </>
                        ) : (
                            <>
                                <button
                                    type="button"
                                    className="btn-cancel"
                                    onClick={onClose}
                                    disabled={isLoading}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn-primary"
                                    disabled={isLoading}
                                >
                                    {isLoading ? 'Saving...' : (isEditMode ? 'Update Travel Period' : 'Save Travel Period')}
                                </button>
                            </>
                        )}
                    </div>
                </form>
            </div>

            {/* Location Picker Modal */}
            <LocationPickerModal
                isOpen={showLocationPicker}
                onClose={() => setShowLocationPicker(false)}
                onSave={handleLocationSave}
                currentLocation={selectedLocation}
                mode="travel"
                title="Select Travel Destination"
            />
        </div>
    );
};

export default TravelPeriodModal;
